/*
 * Назначение: вкладка «Внешний вид виджета» в /settings/appearance.
 * Функционал: настройка четырёх цветов виджета (color-picker + hex),
 * загрузка аватарки в шапку файлом (jpg/png/webp ≤ 2 МБ, ресайз до 512×512
 * на сервере), текст статуса под именем (по умолчанию «онлайн»).
 * Сохраняет цвета и текст через PATCH /api/admin/projects/current. Аватарка
 * загружается отдельным multipart-запросом POST /current/avatar и удаляется
 * через DELETE /current/avatar — без относительных URL и без хостинга
 * картинок где-то снаружи. Превью виджета — рядом с настройками.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ImagePlus, Loader2, RotateCcw, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { projectsApi } from "../../../lib/admin-api";
import { ApiError } from "../../../lib/api";
import type { ProjectDetails, ProjectUpdate, WidgetTheme } from "../../../lib/types";
import { useProject } from "../../components/project/project-provider";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";

/** Дефолтные цвета — должны совпадать с DEFAULT_THEME на бэке (api/endpoints/widget.py). */
export const DEFAULT_THEME: Required<WidgetTheme> = {
  header_bg: "#5b4cdb",
  header_fg: "#ffffff",
  chat_bg: "#ffffff",
  chat_fg: "#1a1a2e",
};

/** Лимит размера файла аватарки (должен совпадать с avatar_max_mb на бэке). */
const AVATAR_MAX_MB = 2;
const AVATAR_ALLOWED_MIME = ["image/jpeg", "image/png", "image/webp"];

/** Описание полей цветов для отрисовки списком и как меток. */
const FIELDS: Array<{
  key: keyof WidgetTheme;
  label: string;
  hint: string;
}> = [
  { key: "header_bg", label: "Фон шапки и кнопок", hint: "Основной акцентный цвет" },
  { key: "header_fg", label: "Цвет текста в шапке и иконок", hint: "Контраст к фону шапки" },
  { key: "chat_bg", label: "Фон чата", hint: "Цвет области сообщений" },
  { key: "chat_fg", label: "Цвет текста сообщений", hint: "Цвет текста ответов ИИ" },
];

const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

function expandHex(value: string): string {
  /** Расширить #abc → #aabbcc для color-picker, который принимает только полный формат. */
  const v = value.trim();
  if (/^#[0-9a-fA-F]{3}$/.test(v)) {
    return "#" + v.slice(1).split("").map((c) => c + c).join("");
  }
  return v.toLowerCase();
}

interface FormState {
  theme: Required<WidgetTheme>;
  online_label: string;
  widget_auto_open_desktop: boolean;
  widget_auto_open_mobile: boolean;
}

function buildFormState(project: ProjectDetails): FormState {
  return {
    theme: {
      header_bg: project.theme.header_bg ?? DEFAULT_THEME.header_bg,
      header_fg: project.theme.header_fg ?? DEFAULT_THEME.header_fg,
      chat_bg: project.theme.chat_bg ?? DEFAULT_THEME.chat_bg,
      chat_fg: project.theme.chat_fg ?? DEFAULT_THEME.chat_fg,
    },
    online_label: project.online_label ?? "",
    widget_auto_open_desktop: Boolean(project.widget_auto_open_desktop),
    widget_auto_open_mobile: Boolean(project.widget_auto_open_mobile),
  };
}

export function AppearanceTab({ project }: { project: ProjectDetails }) {
  const { currentSlug } = useProject();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState>(() => buildFormState(project));
  const [errors, setErrors] = useState<Partial<Record<keyof WidgetTheme, string>>>({});

  // Перезагружаем форму, если данные проекта обновились извне
  useEffect(() => {
    setForm(buildFormState(project));
  }, [project]);

  const onProjectUpdated = (updated: ProjectDetails) => {
    queryClient.setQueryData(["project", currentSlug, "details"], updated);
    queryClient.invalidateQueries({ queryKey: ["projects"] });
  };

  const mutation = useMutation({
    mutationFn: (payload: ProjectUpdate) => projectsApi.update(payload),
    onSuccess: (updated) => {
      toast.success("Внешний вид сохранён");
      onProjectUpdated(updated);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.detail : "Не удалось сохранить");
    },
  });

  const setColor = (key: keyof WidgetTheme, value: string) => {
    setForm((s) => ({ ...s, theme: { ...s.theme, [key]: value } }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const handleSave = () => {
    // Валидация: проверяем все 4 цвета
    const newErrors: typeof errors = {};
    for (const { key, label } of FIELDS) {
      const value = form.theme[key].trim();
      if (!HEX_RE.test(value)) {
        newErrors[key] = `«${label}»: укажи hex-код в формате #RRGGBB`;
      }
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const themeNormalized: WidgetTheme = {
      header_bg: expandHex(form.theme.header_bg),
      header_fg: expandHex(form.theme.header_fg),
      chat_bg: expandHex(form.theme.chat_bg),
      chat_fg: expandHex(form.theme.chat_fg),
    };

    mutation.mutate({
      theme: themeNormalized,
      online_label: form.online_label.trim() || null,
      widget_auto_open_desktop: form.widget_auto_open_desktop,
      widget_auto_open_mobile: form.widget_auto_open_mobile,
    });
  };

  const handleResetColors = () => {
    setForm((s) => ({ ...s, theme: { ...DEFAULT_THEME } }));
    setErrors({});
  };

  /** Совпадает ли текущая тема с дефолтом — для блокировки кнопки «Стандартные цвета». */
  const isDefaultTheme = useMemo(() => {
    return (Object.keys(DEFAULT_THEME) as Array<keyof WidgetTheme>).every(
      (k) => form.theme[k]?.toLowerCase() === DEFAULT_THEME[k],
    );
  }, [form.theme]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Левая колонка — форма */}
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Цвета виджета</CardTitle>
            <CardDescription>
              Цвета настраиваются индивидуально для этого проекта. Изменения
              применяются сразу после сохранения — пересобирать виджет не нужно.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {FIELDS.map(({ key, label, hint }) => (
              <div key={key} className="space-y-1.5">
                <Label htmlFor={`color-${key}`}>{label}</Label>
                <div className="flex items-center gap-2">
                  <input
                    id={`color-${key}`}
                    type="color"
                    className="h-10 w-12 rounded border border-input cursor-pointer bg-transparent p-0"
                    value={expandHex(form.theme[key])}
                    onChange={(e) => setColor(key, e.target.value)}
                  />
                  <Input
                    className="font-mono text-sm uppercase"
                    value={form.theme[key]}
                    onChange={(e) => setColor(key, e.target.value)}
                    placeholder="#RRGGBB"
                    spellCheck={false}
                    maxLength={7}
                  />
                </div>
                <p className="text-xs text-muted-foreground">{hint}</p>
                {errors[key] && (
                  <p className="text-xs text-destructive">{errors[key]}</p>
                )}
              </div>
            ))}

            <div className="border-t pt-4 space-y-4">
              <div>
                <Label htmlFor="online_label">Подпись о статусе под именем</Label>
                <Input
                  id="online_label"
                  className="mt-1.5"
                  value={form.online_label}
                  onChange={(e) => setForm((s) => ({ ...s, online_label: e.target.value }))}
                  placeholder="онлайн"
                  maxLength={64}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Например: «онлайн», «отвечу за минуту», «работаем 9–21». Если
                  пусто — подпись не показывается.
                </p>
              </div>

              <div className="rounded-md border p-3 space-y-4">
                <div>
                  <Label className="text-sm font-medium">
                    Открывать чат сразу при загрузке сайта
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Раздельно для компьютеров и телефонов. На телефоне
                    развёрнутый чат перекрывает сайт, поэтому можно оставить
                    его закрытым на мобильных, но открытым на ПК. Не влияет на
                    встроенный блок чата в вёрстке — он всегда открыт.
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <Switch
                    id="auto_open_desktop"
                    checked={form.widget_auto_open_desktop}
                    onCheckedChange={(v) =>
                      setForm((s) => ({ ...s, widget_auto_open_desktop: Boolean(v) }))
                    }
                  />
                  <div className="flex-1 -mt-0.5">
                    <Label htmlFor="auto_open_desktop" className="cursor-pointer">
                      На компьютере — открыт при входе
                    </Label>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Switch
                    id="auto_open_mobile"
                    checked={form.widget_auto_open_mobile}
                    onCheckedChange={(v) =>
                      setForm((s) => ({ ...s, widget_auto_open_mobile: Boolean(v) }))
                    }
                  />
                  <div className="flex-1 -mt-0.5">
                    <Label htmlFor="auto_open_mobile" className="cursor-pointer">
                      На мобильном — открыт при входе
                    </Label>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-2 flex-wrap">
              <Button onClick={handleSave} disabled={mutation.isPending}>
                {mutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Сохранить
              </Button>
              <Button
                variant="outline"
                onClick={handleResetColors}
                disabled={isDefaultTheme || mutation.isPending}
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Стандартные цвета
              </Button>
            </div>
          </CardContent>
        </Card>

        <AvatarCard project={project} onUpdated={onProjectUpdated} />
      </div>

      {/* Правая колонка — превью */}
      <Card>
        <CardHeader>
          <CardTitle>Превью</CardTitle>
          <CardDescription>
            Так виджет будет выглядеть на сайте посетителя. Реальный размер
            зависит от ширины экрана.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <WidgetPreview
            theme={form.theme}
            avatarUrl={project.avatar_url}
            onlineLabel={form.online_label}
            projectName={project.name}
            greeting={project.greeting_text}
            quickReplies={project.quick_replies}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function AvatarCard({
  project,
  onUpdated,
}: {
  project: ProjectDetails;
  onUpdated: (updated: ProjectDetails) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<"upload" | "delete" | null>(null);

  // Превью текущей аватарки. На странице админки путь относительный,
  // поэтому дополняем своим origin-ом — иначе картинка не загрузится
  // (например, при просмотре через прокси на другом домене).
  const previewUrl = useMemo(() => buildAbsoluteUrl(project.avatar_url), [project.avatar_url]);

  const handlePick = () => fileInputRef.current?.click();

  const handleFile = async (file: File) => {
    if (!AVATAR_ALLOWED_MIME.includes(file.type)) {
      toast.error("Только JPG, PNG или WEBP");
      return;
    }
    if (file.size > AVATAR_MAX_MB * 1024 * 1024) {
      toast.error(`Файл больше ${AVATAR_MAX_MB} МБ`);
      return;
    }
    setBusy("upload");
    try {
      const updated = await projectsApi.uploadAvatar(file);
      onUpdated(updated);
      toast.success("Аватарка загружена");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.detail : "Не удалось загрузить");
    } finally {
      setBusy(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async () => {
    setBusy("delete");
    try {
      const updated = await projectsApi.deleteAvatar();
      onUpdated(updated);
      toast.success("Аватарка удалена");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.detail : "Не удалось удалить");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Аватарка в шапке виджета</CardTitle>
        <CardDescription>
          Квадратная картинка лучше всего. Файл загрузится на наш сервер,
          уменьшится до 512×512 и пересохранится в WEBP — это нормализует
          размер и ускорит загрузку у посетителей сайта.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-4">
          <div className="h-20 w-20 rounded-full overflow-hidden bg-muted border flex items-center justify-center shrink-0">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Аватарка"
                className="h-full w-full object-cover"
              />
            ) : (
              <ImagePlus className="h-8 w-8 text-muted-foreground" />
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
              }}
            />
            <Button onClick={handlePick} disabled={busy !== null}>
              {busy === "upload" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ImagePlus className="mr-2 h-4 w-4" />
              )}
              {project.avatar_url ? "Заменить" : "Загрузить"}
            </Button>
            {project.avatar_url && (
              <Button
                variant="outline"
                onClick={handleDelete}
                disabled={busy !== null}
              >
                {busy === "delete" ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="mr-2 h-4 w-4" />
                )}
                Удалить
              </Button>
            )}
          </div>
        </div>
        <Alert>
          <AlertDescription className="text-xs">
            Поддерживаются JPG, PNG, WEBP. Размер файла — до {AVATAR_MAX_MB} МБ.
            Если хочется убрать аватарку — нажми «Удалить».
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
}

/** Привести относительный путь /media/... к абсолютному URL текущего origin. */
function buildAbsoluteUrl(value: string | null): string | null {
  if (!value) return null;
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  if (value.startsWith("/")) return window.location.origin + value;
  return value;
}

interface PreviewProps {
  theme: Required<WidgetTheme>;
  avatarUrl: string | null;
  onlineLabel: string;
  projectName: string;
  greeting: string | null;
  quickReplies: string[];
}

function WidgetPreview({
  theme,
  avatarUrl,
  onlineLabel,
  projectName,
  greeting,
  quickReplies,
}: PreviewProps) {
  const headerBg = HEX_RE.test(theme.header_bg) ? theme.header_bg : DEFAULT_THEME.header_bg;
  const headerFg = HEX_RE.test(theme.header_fg) ? theme.header_fg : DEFAULT_THEME.header_fg;
  const chatBg = HEX_RE.test(theme.chat_bg) ? theme.chat_bg : DEFAULT_THEME.chat_bg;
  const chatFg = HEX_RE.test(theme.chat_fg) ? theme.chat_fg : DEFAULT_THEME.chat_fg;

  const previewAvatar = buildAbsoluteUrl(avatarUrl);

  return (
    <div
      className="rounded-xl overflow-hidden border shadow-md mx-auto"
      style={{ maxWidth: 360, background: chatBg, color: chatFg }}
    >
      {/* Шапка */}
      <div
        className="flex items-center justify-between gap-3 px-4 py-3"
        style={{ background: headerBg, color: headerFg }}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          {previewAvatar ? (
            <img
              src={previewAvatar}
              alt=""
              className="h-9 w-9 rounded-full object-cover bg-white/20 shrink-0"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
          ) : null}
          <div className="flex flex-col min-w-0 leading-tight">
            <span className="font-semibold text-sm truncate">{projectName || "Чат"}</span>
            {onlineLabel.trim() && (
              <span className="text-xs opacity-85 inline-flex items-center gap-1.5 truncate">
                <span
                  className="inline-block h-2 w-2 rounded-full shrink-0"
                  style={{ background: "#22c55e", boxShadow: "0 0 0 2px rgba(34,197,94,0.25)" }}
                />
                {onlineLabel.trim()}
              </span>
            )}
          </div>
        </div>
        <div
          className="h-9 w-9 rounded-full flex items-center justify-center text-base font-semibold shrink-0"
          style={{ background: "rgba(255,255,255,0.18)" }}
        >
          ✕
        </div>
      </div>

      {/* Лента сообщений */}
      <div className="px-4 py-3 space-y-2.5" style={{ minHeight: 200 }}>
        {greeting && (
          <div
            className="self-start rounded-lg px-3 py-2 text-sm max-w-[85%]"
            style={{ background: "rgba(0,0,0,0.05)", color: chatFg }}
          >
            {greeting}
          </div>
        )}
        <div
          className="ml-auto rounded-lg px-3 py-2 text-sm max-w-[85%]"
          style={{ background: headerBg, color: headerFg, width: "fit-content" }}
        >
          Сколько стоит лендинг?
        </div>
        {quickReplies.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {quickReplies.slice(0, 3).map((q) => (
              <span
                key={q}
                className="px-3 py-1 rounded-full text-xs border"
                style={{ borderColor: headerBg, color: chatFg }}
              >
                {q}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Поле ввода */}
      <div className="border-t px-3 py-2 flex items-center gap-2" style={{ background: chatBg }}>
        <div
          className="flex-1 rounded-md border px-3 py-1.5 text-sm"
          style={{ color: chatFg, opacity: 0.6 }}
        >
          Напиши сообщение…
        </div>
        <div
          className="h-8 w-8 rounded-md flex items-center justify-center"
          style={{ background: headerBg, color: headerFg }}
        >
          ➤
        </div>
      </div>
    </div>
  );
}
